import React, { useEffect, useRef } from 'react'
import IPModal from '../../../components/IPModal'
import { BaseComponentWithContextProps, componentWithContext } from '../../../base/customHooks/componentHOC'
import { ServiceRouter } from '../../../base/proxy/serviceRouter'
import { GetVideosByCategoryApi } from '../services/getVideosByCategoryApi'
import { GetMyVideoProgressApi } from '../services/getMyVideoProgressApi'
import { SaveVideoProgressApi } from '../services/saveVideoProgressApi'
import { installScormApiShim } from '../utils/scormApiShim'
import Swal from 'sweetalert2'
import { IVideo, IVideoWatchProgress, ISaveVideoProgressRequest } from '../models/models'
import { useCustomReducer } from '../../../base/customHooks'

interface IVideoShowcaseProps extends BaseComponentWithContextProps {
  categoryId: number
  onBack?: () => void
}

interface IVideoShowcaseState {
  videos: IVideo[]
  progressByVideoId: { [videoId: number]: IVideoWatchProgress }
  selectedVideo?: IVideo
  maximized: boolean
}

const toEmbedUrl = (url: string): string => {
  if (!url) return ''

  const youtubeWatch = url.match(/[?&]v=([^&]+)/)
  if (url.includes('youtube.com') && youtubeWatch) return `https://www.youtube.com/embed/${youtubeWatch[1]}`

  const youtuBe = url.match(/youtu\.be\/([^?&]+)/)
  if (youtuBe) return `https://www.youtube.com/embed/${youtuBe[1]}`

  const vimeo = url.match(/vimeo\.com\/(\d+)/)
  if (vimeo && !url.includes('player.vimeo.com')) return `https://player.vimeo.com/video/${vimeo[1]}`

  return url
}

// FileUrl/ThumbnailUrl arrive Base64-obfuscated from the public endpoint; decode before use.
const decodeUrl = (v?: string): string => {
  if (!v) return ''
  try {
    return new TextDecoder().decode(Uint8Array.from(window.atob(v), c => c.charCodeAt(0)))
  } catch {
    return v
  }
}

const PROGRESS_SEND_INTERVAL_MS = 3000
const RESUME_END_BUFFER_SECONDS = 2

function VideoShowcase(props: IVideoShowcaseProps) {
  const { categoryId } = props
  const lastProgressSentAt = useRef(0)
  const scormCleanupRef = useRef<(() => void) | null>(null)
  const videoElRef = useRef<HTMLVideoElement | null>(null)
  const resumeSecondsRef = useRef(0)
  const flushProgressRef = useRef<() => void>(() => { })
  const scormSentSessionSecondsRef = useRef(0)
  const quizAttemptSentRef = useRef(false)

  const [state, setState] = useCustomReducer<IVideoShowcaseState>({ videos: [], progressByVideoId: {}, maximized: false })

  useEffect(() => {
    getVideos()
  }, [categoryId])

  useEffect(() => {
    return () => {
      flushProgressRef.current()
      scormCleanupRef.current && scormCleanupRef.current()
    }
  }, [])

  const getVideos = async () => {
    props.showLoading()
    const api: GetVideosByCategoryApi = new GetVideosByCategoryApi({})
    api
      .getVideosByCategory(categoryId)
      .then((res: IVideo[]) => {
        setState({ videos: res })
        getMyProgress()
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
  }

  const getMyProgress = async () => {
    const api = new GetMyVideoProgressApi({})
    api
      .getMyProgress(categoryId)
      .then((res: IVideoWatchProgress[]) => {
        const map: { [videoId: number]: IVideoWatchProgress } = {}
        res.forEach(p => (map[p.VideoId] = p))
        setState({ progressByVideoId: map })
        props.hideLoading()
      })
      .catch(() => {
        props.hideLoading()
      })
  }

  const persistProgress = (videoId: number, request: ISaveVideoProgressRequest, localPercent: number, completed: boolean) => {
    const api = new SaveVideoProgressApi({})
    api.saveProgress(request)
    const prev = state.progressByVideoId[videoId]
    setState({
      progressByVideoId: {
        ...state.progressByVideoId,
        [videoId]: {
          VideoId: videoId,
          WatchedSeconds: request.WatchedSeconds ?? prev?.WatchedSeconds ?? 0,
          DurationSeconds: request.DurationSeconds ?? prev?.DurationSeconds ?? 0,
          WatchPercent: completed ? 100 : Math.max(localPercent, prev?.WatchPercent ?? 0),
          Completed: completed || (prev?.Completed ?? false),
          ScormLessonLocation: request.ScormLessonLocation ?? prev?.ScormLessonLocation ?? '',
          ScormSuspendData: request.ScormSuspendData ?? prev?.ScormSuspendData ?? '',
          LastWatchedDate: new Date(),
          CompletedDate: completed ? new Date() : prev?.CompletedDate,
        },
      },
    })
  }

  const saveVideoFileProgress = (videoId: number, watchedSeconds: number, durationSeconds: number, completed: boolean) => {
    const percent = durationSeconds > 0 ? Math.min(100, (watchedSeconds / durationSeconds) * 100) : completed ? 100 : 0
    persistProgress(
      videoId,
      { VideoId: videoId, WatchedSeconds: watchedSeconds, DurationSeconds: durationSeconds, Completed: completed },
      percent,
      completed
    )
  }

  const flushVideoProgress = () => {
    const video = state.selectedVideo
    const el = videoElRef.current
    if (!video || video.SourceType !== 0 || !el) return
    saveVideoFileProgress(video.Id, el.currentTime, el.duration || 0, el.ended)
  }
  flushProgressRef.current = flushVideoProgress

  const handleVideoTimeUpdate = (video: IVideo, e: React.SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget
    const now = Date.now()
    if (now - lastProgressSentAt.current < PROGRESS_SEND_INTERVAL_MS) return
    lastProgressSentAt.current = now
    saveVideoFileProgress(video.Id, el.currentTime, el.duration || 0, false)
  }

  const handleVideoEnded = (video: IVideo, e: React.SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget
    saveVideoFileProgress(video.Id, el.duration || 0, el.duration || 0, true)
  }

  const openVideo = (video: IVideo) => {
    const progress = state.progressByVideoId[video.Id]
    resumeSecondsRef.current =
      progress && progress.DurationSeconds > 0 && progress.WatchedSeconds < progress.DurationSeconds - RESUME_END_BUFFER_SECONDS
        ? progress.WatchedSeconds
        : 0
    lastProgressSentAt.current = 0
    scormSentSessionSecondsRef.current = 0
    quizAttemptSentRef.current = false

    setState({ selectedVideo: video })

    if (video.SourceType === 2) {
      scormCleanupRef.current = installScormApiShim(
        {
          lessonLocation: progress?.ScormLessonLocation,
          suspendData: progress?.ScormSuspendData,
          completed: progress?.Completed,
        },
        scorm => {
          if (!scorm.started && !scorm.completed) return

          const sessionDelta = Math.max(0, scorm.sessionSeconds - scormSentSessionSecondsRef.current)
          if (sessionDelta > 0) scormSentSessionSecondsRef.current = scorm.sessionSeconds

          const hasQuizData =
            scorm.cmi['cmi.core.score.raw'] != null ||
            scorm.cmi['cmi.score.raw'] != null ||
            scorm.cmi['cmi.score.scaled'] != null ||
            Object.keys(scorm.cmi).some(k => k.startsWith('cmi.interactions.'))
          const isNewQuizAttempt = scorm.completed && hasQuizData && !quizAttemptSentRef.current
          if (isNewQuizAttempt) quizAttemptSentRef.current = true

          persistProgress(
            video.Id,
            {
              VideoId: video.Id,
              WatchPercent: scorm.percent ?? undefined,
              SessionSeconds: sessionDelta > 0 ? sessionDelta : undefined,
              Completed: scorm.completed,
              ScormLessonLocation: scorm.lessonLocation || undefined,
              ScormSuspendData: scorm.suspendData || undefined,
              ScormCmiData: Object.keys(scorm.cmi).length > 0 ? JSON.stringify(scorm.cmi) : undefined,
              QuizAttempt: isNewQuizAttempt ? JSON.stringify(scorm.cmi) : undefined,
            },
            scorm.percent ?? 0,
            scorm.completed
          )
        }
      )
    }
  }

  const closeVideo = () => {
    flushVideoProgress()
    if (scormCleanupRef.current) {
      scormCleanupRef.current()
      scormCleanupRef.current = null
    }
    setState({ selectedVideo: undefined, maximized: false })
  }

  const categoryName = state.videos.length > 0 ? state.videos[0].VideoCategoryName : ''

  return (
    <div className='container current-account'>
      <div className='content col-lg-12 m-auto'>
        <br></br>
        <div className='card-body'>
          <div className='' style={{ textAlign: 'center', fontSize: '20px' }}>
            <span className='h5'>{categoryName ? `${categoryName} EĞİTİM DOKÜMANLARI` : 'EĞİTİM DOKÜMANLARI'}</span>
          </div>
          {props.onBack && (
            <div className='m-t-10' style={{ textAlign: 'center' }}>
              <span className='linkDpp cursor-pointer' onClick={() => props.onBack && props.onBack()}>
                &larr; Geri Dön
              </span>
            </div>
          )}

          <div className='row m-t-20'>
            {state.videos.length === 0 && (
              <div className='col-12 text-center m-t-20'>
                <span>Bu kategoriye ait video bulunmamaktadır.</span>
              </div>
            )}
            {state.videos.map((video, index) => {
              const progress = state.progressByVideoId[video.Id]
              const thumbnailUrl = decodeUrl(video.ThumbnailUrl)

              return (
                <div className='col-lg-6 card-mobile-resize m-t-20' key={index}>
                  <div
                    className='video-card cursor-pointer'
                    style={{
                      minHeight: '300px',
                      height: '300px',
                      position: 'relative',
                      padding: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onClick={() => openVideo(video)}
                  >
                    {progress?.Completed && (
                      <span
                        className='badge'
                        style={{ position: 'absolute', top: 10, right: 10, backgroundColor: '#4caf50', color: '#fff', padding: '5px 10px', borderRadius: '10px', zIndex: 1 }}
                      >
                        <i className='fas fa-check' /> Tamamlandı
                      </span>
                    )}
                    <div
                      className='text-center'
                      style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, overflow: 'hidden' }}
                    >
                      {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt={video.Title} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      ) : (
                        <i className='fas fa-play-circle' style={{ fontSize: '110px', color: '#4caf50' }} />
                      )}
                    </div>
                    <div className='text-center' style={{ flex: '0 0 auto' }}>
                      <h5 className='m-b-0'>{video.Title}</h5>
                      {video.Description && <p className='m-b-0'>{video.Description}</p>}
                      {progress && !progress.Completed && (
                        <div style={{ width: '80%', margin: '10px auto 0' }}>
                          <div style={{ height: '6px', backgroundColor: '#eee', borderRadius: '3px' }}>
                            <div
                              style={{
                                height: '6px',
                                width: `${Math.min(100, progress.WatchPercent)}%`,
                                backgroundColor: '#4caf50',
                                borderRadius: '3px',
                              }}
                            />
                          </div>
                          <span style={{ fontSize: '12px', color: '#666' }}>%{Math.round(progress.WatchPercent)} izlendi</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <IPModal
        show={!!state.selectedVideo}
        hideConfirm={true}
        width={state.selectedVideo?.SourceType === 2 ? 1100 : 800}
        title={state.selectedVideo?.Title}
        onClose={() => closeVideo()}
        maximized={state.maximized}
        onToggleMaximize={() => setState({ maximized: !state.maximized })}
      >
        <div className='text-center'>
          {state.selectedVideo?.SourceType === 2 ? (
            <iframe
              src={`${ServiceRouter.getServiceUrl()}Scorm/${state.selectedVideo?.Id}/${state.selectedVideo?.ScormLaunchPath}`}
              style={{ width: '100%', height: state.maximized ? '100%' : '650px', border: 'none', display: 'block' }}
              allowFullScreen
              title={state.selectedVideo?.Title}
              sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-modals'
            />
          ) : state.selectedVideo?.SourceType === 1 ? (
            <iframe
              src={toEmbedUrl(state.selectedVideo?.ExternalUrl || '')}
              style={{ width: '100%', height: state.maximized ? '100%' : '450px', border: 'none', display: 'block' }}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
              title={state.selectedVideo?.Title}
            />
          ) : (
            <video
              ref={videoElRef}
              controls
              style={state.maximized ? { width: '100%', height: '100%', display: 'block' } : { width: '100%', maxHeight: '450px' }}
              src={decodeUrl(state.selectedVideo?.FileUrl)}
              onLoadedMetadata={e => {
                if (resumeSecondsRef.current > 0) e.currentTarget.currentTime = resumeSecondsRef.current
              }}
              onTimeUpdate={e => state.selectedVideo && handleVideoTimeUpdate(state.selectedVideo, e)}
              onPause={() => flushVideoProgress()}
              onEnded={e => state.selectedVideo && handleVideoEnded(state.selectedVideo, e)}
            >
              Tarayıcınız video oynatmayı desteklemiyor.
            </video>
          )}
        </div>
      </IPModal>
    </div>
  )
}
export default componentWithContext(VideoShowcase)
